import React from 'react';
import _get from 'lodash/get';
import { transparentize } from 'polished';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MappingProps from 'types/Mapping';

import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import CancelIcon from '@material-ui/icons/Cancel';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import MappingsRow from 'components/Mappings/MappingsRow';

interface Props {
  mappings: MappingProps[];
  mappingIndex: number;
  onDeleteMapping: (index: number) => void;
  onUpdateMapping: (index: number, newMapping: any) => void;
  onClose: () => void;
}

function PositionIdMapping({
  mappings,
  mappingIndex,
  onDeleteMapping,
  onUpdateMapping,
  onClose,
}: Props) {
  const classes = useStyles();

  const handleDelete = () => {
    onDeleteMapping(mappingIndex);
    onClose();
  };

  return (
    <div className={classes.root}>
      <div className={classes.sectionHeader}>
        <div className={classes.textContainer}>
          <Typography className={classes.title} variant="body2">
            Person Identifier
          </Typography>
          <Typography className={classes.description} variant="caption">
            If your file contains people who have multiple positions, and you’d like each position
            to be imported separately, you need to include one row per position instead of one row
            per person. Then, set your unique identifier above to the “Position Id” (unique for
            every row), and the identifier below to the “Person Id” (unique for every person) so
            that we can properly tie people to their multiple positions. Learn more about
            position-based imports{' '}
            <Link href="/" color="secondary">
              here{' '}
            </Link>
            .
          </Typography>
        </div>

        <IconButton className={classes.closeButton} onClick={handleDelete}>
          <CancelIcon />
        </IconButton>
      </div>
      <Table className={classes.table}>
        {mappings.map((mapping: MappingProps, index: number) => (
          <MappingsRow
            key={index}
            mapping={mapping}
            rowIndex={mappingIndex}
            onDeleteMapping={onDeleteMapping}
            onUpdateMapping={onUpdateMapping}
            selectorBackgroundColor="#f5f5f5"
          />
        ))}
      </Table>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  textContainer: {
    paddingTop: theme.spacing(),
    paddingLeft: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(),
  },
  description: {
    color: transparentize(0.4, '#000000'),
  },
  table: {
    tableLayout: 'fixed',
  },
  closeButton: {
    margin: theme.spacing(),
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  clearButtonCell: {
    paddingBottom: 0,
  },
}));

export default PositionIdMapping;
